export type PublicKeyJSON = Record<string, unknown> & {
  challenge: string
  user?: { id: string } & Record<string, unknown>
  allowCredentials?: Array<{ id: string } & Record<string, unknown>>
  excludeCredentials?: Array<{ id: string } & Record<string, unknown>>
}

const fromBase64URL = (value: string): ArrayBuffer => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const binary = atob(padded)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0)).buffer
}

const toBase64URL = (value: ArrayBuffer): string => {
  const binary = String.fromCharCode(...new Uint8Array(value))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const decodeCreationOptions = (value: { publicKey?: PublicKeyJSON } | PublicKeyJSON): PublicKeyCredentialCreationOptions => {
  const source = ('publicKey' in value ? value.publicKey : value) as PublicKeyJSON
  return {
    ...source,
    challenge: fromBase64URL(source.challenge),
    user: source.user ? { ...source.user, id: fromBase64URL(source.user.id) } : undefined,
    excludeCredentials: source.excludeCredentials?.map((item) => ({ ...item, id: fromBase64URL(item.id) })),
  } as PublicKeyCredentialCreationOptions
}

export const decodeRequestOptions = (value: { publicKey?: PublicKeyJSON } | PublicKeyJSON): PublicKeyCredentialRequestOptions => {
  const source = ('publicKey' in value ? value.publicKey : value) as PublicKeyJSON
  return {
    ...source,
    challenge: fromBase64URL(source.challenge),
    allowCredentials: source.allowCredentials?.map((item) => ({ ...item, id: fromBase64URL(item.id) })),
  } as PublicKeyCredentialRequestOptions
}

export const serializeCredential = (credential: PublicKeyCredential): Record<string, unknown> => {
  const response = credential.response
  const base = {
    id: credential.id,
    rawId: toBase64URL(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
  }
  if (response instanceof AuthenticatorAttestationResponse) {
    return {
      ...base,
      response: {
        attestationObject: toBase64URL(response.attestationObject),
        clientDataJSON: toBase64URL(response.clientDataJSON),
        transports: response.getTransports?.() ?? [],
      },
    }
  }
  const assertion = response as AuthenticatorAssertionResponse
  return {
    ...base,
    response: {
      authenticatorData: toBase64URL(assertion.authenticatorData),
      clientDataJSON: toBase64URL(assertion.clientDataJSON),
      signature: toBase64URL(assertion.signature),
      userHandle: assertion.userHandle ? toBase64URL(assertion.userHandle) : null,
    },
  }
}
